import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * Vangt een uitzondering uit de render van zijn kinderen op.
 *
 * Zonder zo'n grens haalt React de hele boom weg bij een fout in één component, en
 * dan blijft er een witte pagina over. Dat is precies wat er gebeurde toen de
 * drilldown in het verbruikslog een veld las dat een oudere API niet meestuurde: de
 * dev-server heeft geen foutoverlay (hmr.overlay staat uit in vite.config.ts), dus
 * er was niets te zien -- geen melding, geen tabblad, niets om verder te klikken.
 *
 * Dit moet een klassecomponent zijn; componentDidCatch bestaat niet als hook.
 *
 * WAT DIT NIET IS. Een grens vangt alleen fouten uit render, uit een levenscyclus en
 * uit een constructor. Een afgewezen fetch in een handler of een effect komt hier
 * niet langs -- die hoort ter plekke opgevangen te worden, zoals de tabbladen met
 * ApiFout en een toast al doen.
 */
interface Props {
  children: ReactNode;
  /**
   * Waar de fout in zit, voor de melding. Bijvoorbeeld de naam van het tabblad;
   * zonder dit staat er "dit onderdeel".
   */
  naam?: string;
  /**
   * De klassen van de container die deze grens vervangt.
   *
   * De grens rendert zelf de omhullende div, zodat hij in de plaats van die div kan
   * staan in plaats van er een niveau bij te zetten. De melding komt daarmee ook op
   * de plek en de breedte van de inhoud die faalde.
   */
  className?: string;
}

interface State {
  fout: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { fout: null };

  static getDerivedStateFromError(fout: Error): State {
    return { fout };
  }

  override componentDidCatch(fout: Error, info: ErrorInfo): void {
    // In de console, want de frontend heeft geen logkanaal naar de server. De
    // componentStack staat erbij: die zegt in welk tabblad het misging, en dat is
    // niet uit de melding zelf te halen.
    console.error('Onderbroken render opgevangen', fout, info.componentStack);
  }

  private herstel = (): void => {
    this.setState({ fout: null });
  };

  override render(): ReactNode {
    const { children, naam, className } = this.props;
    const { fout } = this.state;

    if (!fout) {
      return <div className={className}>{children}</div>;
    }

    return (
      <div className={className}>
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {naam ? `${naam} kon niet worden getoond` : 'Dit onderdeel kon niet worden getoond'}
            </CardTitle>
            <CardDescription>
              Er ging iets mis bij het opbouwen van dit paneel. De rest van het scherm werkt nog:
              kies een ander onderdeel, of probeer het hier opnieuw.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/*
              De technische melding staat erbij en niet alleen in de console. Dit is
              een beheerscherm voor iSOFT zelf, en het verschil tussen "er ging iets
              mis" en de melding is het verschil tussen een raadsel en een
              aanwijzing.
            */}
            <p className="text-xs font-mono bg-muted rounded p-2 break-words">
              {fout.message || fout.name || 'Onbekende fout'}
            </p>
            <Button variant="outline" size="sm" onClick={this.herstel}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Opnieuw proberen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
